import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpenseCategorySelect } from '@/components/shared/expense-category-select';
import { FundSelect } from '@/components/shared/fund-select';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox, type ListboxOption } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { useChurchStore } from '@/stores/church-store';

export type BudgetLineDraft = {
  key: string;
  lineType: 'income' | 'expense';
  fundId: string | null;
  categoryId: string | null;
  plannedAmountMinor: number | null;
  notes: string;
};

export type BudgetFormValues = {
  name: string;
  periodStart: string | null;
  periodEnd: string | null;
  lines: BudgetLineDraft[];
};

export type BudgetFormSubmit = {
  name: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  lines: Array<{
    fundId?: string | null;
    categoryId?: string | null;
    plannedAmountMinor: number;
    currency: string;
    notes?: string | null;
  }>;
};

const newLine = (lineType: 'income' | 'expense' = 'income'): BudgetLineDraft => ({
  key: crypto.randomUUID(),
  lineType,
  fundId: null,
  categoryId: null,
  plannedAmountMinor: null,
  notes: '',
});

export interface BudgetFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initial?: BudgetFormValues;
  submitting?: boolean;
  onSubmit: (values: BudgetFormSubmit) => void;
}

export const BudgetFormDialog = ({
  open,
  onOpenChange,
  mode,
  initial,
  submitting = false,
  onSubmit,
}: BudgetFormDialogProps) => {
  const { t } = useTranslation('spending');
  const church = useChurchStore((state) => state.church);
  const currency = church?.baseCurrency ?? 'GHS';

  const [name, setName] = useState(initial?.name ?? '');
  const [periodStart, setPeriodStart] = useState<string | null>(initial?.periodStart ?? null);
  const [periodEnd, setPeriodEnd] = useState<string | null>(initial?.periodEnd ?? null);
  const [lines, setLines] = useState<BudgetLineDraft[]>(
    initial?.lines?.length ? initial.lines : [newLine()],
  );

  const lineTypeOptions: ListboxOption[] = [
    { value: 'income', label: t('budgets.lineType.income') },
    { value: 'expense', label: t('budgets.lineType.expense') },
  ];

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setPeriodStart(initial?.periodStart ?? null);
    setPeriodEnd(initial?.periodEnd ?? null);
    setLines(initial?.lines?.length ? initial.lines : [newLine()]);
  }, [open, initial]);

  const updateLine = (key: string, patch: Partial<BudgetLineDraft>) => {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
  };

  const canSubmit =
    name.trim().length > 0 &&
    Boolean(periodStart) &&
    Boolean(periodEnd) &&
    lines.length > 0 &&
    lines.every(
      (line) =>
        line.plannedAmountMinor !== null &&
        line.plannedAmountMinor > 0 &&
        (line.lineType === 'income' ? Boolean(line.fundId) : Boolean(line.categoryId)),
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="overflow-visible">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('budgets.create') : t('budgets.edit')}
          </DialogTitle>
          <DialogDescription>{t('budgets.formDescription')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="budget-name">{t('budgets.name')}</Label>
              <Input
                id="budget-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('budgets.periodStart')}</Label>
                <DatePicker
                  value={periodStart}
                  onChange={setPeriodStart}
                  popoverPortal="dialog"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('budgets.periodEnd')}</Label>
                <DatePicker value={periodEnd} onChange={setPeriodEnd} popoverPortal="dialog" />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label>{t('budgets.lines')}</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setLines((current) => [...current, newLine()])}
                >
                  <Plus className="size-4" />
                  {t('budgets.addLine')}
                </Button>
              </div>

              <div className="overflow-hidden rounded-lg border border-border">
                <div className="hidden grid-cols-[2.5rem_minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.1fr)_2.5rem] gap-2 border-b border-border bg-surface-sunken px-3 py-2 text-caption font-medium text-fg-secondary md:grid">
                  <span aria-hidden>#</span>
                  <span>{t('budgets.lineType.label')}</span>
                  <span>{t('budgets.columns.target')}</span>
                  <span>{t('budgets.planned')}</span>
                  <span>{t('budgets.notes')}</span>
                  <span className="sr-only">{t('budgets.removeLine')}</span>
                </div>

                <div className="divide-y divide-border">
                  {lines.map((line, index) => (
                    <div
                      key={line.key}
                      className="grid grid-cols-1 items-end gap-2 px-3 py-3 md:grid-cols-[2.5rem_minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.1fr)_2.5rem] md:items-center"
                    >
                      <div className="flex h-10 items-center text-body-sm font-medium tabular-nums text-fg-secondary">
                        <span className="md:sr-only">{t('budgets.lineNumber', { number: index + 1 })}</span>
                        <span className="hidden md:inline" aria-hidden>
                          {index + 1}
                        </span>
                      </div>
                      <div className="space-y-1.5 md:space-y-0">
                        <Label className="md:sr-only">{t('budgets.lineType.label')}</Label>
                        <Listbox
                          value={line.lineType}
                          onChange={(value) => {
                            if (value !== 'income' && value !== 'expense') return;
                            updateLine(line.key, {
                              lineType: value,
                              fundId: null,
                              categoryId: null,
                            });
                          }}
                          options={lineTypeOptions}
                          popoverPortal="dialog"
                        />
                      </div>
                      <div className="space-y-1.5 md:space-y-0">
                        <Label className="md:sr-only">
                          {line.lineType === 'income' ? t('budgets.fund') : t('budgets.category')}
                        </Label>
                        {line.lineType === 'income' ? (
                          <FundSelect
                            value={line.fundId}
                            onChange={(fundId) => updateLine(line.key, { fundId })}
                            popoverPortal="dialog"
                          />
                        ) : (
                          <ExpenseCategorySelect
                            value={line.categoryId}
                            onChange={(categoryId) => updateLine(line.key, { categoryId })}
                            popoverPortal="dialog"
                          />
                        )}
                      </div>
                      <div className="space-y-1.5 md:space-y-0">
                        <Label className="md:sr-only">{t('budgets.planned')}</Label>
                        <MoneyInput
                          value={line.plannedAmountMinor}
                          onChange={(plannedAmountMinor) =>
                            updateLine(line.key, { plannedAmountMinor })
                          }
                          currency={currency}
                        />
                      </div>
                      <div className="space-y-1.5 md:space-y-0">
                        <Label className="md:sr-only" htmlFor={`budget-line-notes-${line.key}`}>
                          {t('budgets.notes')}
                        </Label>
                        <Input
                          id={`budget-line-notes-${line.key}`}
                          value={line.notes}
                          onChange={(event) =>
                            updateLine(line.key, { notes: event.target.value })
                          }
                        />
                      </div>
                      <div className="flex justify-end md:justify-center">
                        {lines.length > 1 ? (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            aria-label={t('budgets.removeLine')}
                            onClick={() =>
                              setLines((current) =>
                                current.filter((item) => item.key !== line.key),
                              )
                            }
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        ) : (
                          <span className="hidden size-10 md:block" aria-hidden />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('budgets.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || submitting}
            onClick={() => {
              if (!periodStart || !periodEnd || !canSubmit) return;
              onSubmit({
                name: name.trim(),
                periodStart,
                periodEnd,
                currency,
                lines: lines.map((line) => ({
                  fundId: line.lineType === 'income' ? line.fundId : null,
                  categoryId: line.lineType === 'expense' ? line.categoryId : null,
                  plannedAmountMinor: line.plannedAmountMinor!,
                  currency,
                  notes: line.notes.trim() || null,
                })),
              });
            }}
          >
            {mode === 'create' ? t('budgets.create') : t('budgets.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const linesFromBudget = (
  lines: Array<{
    id: string;
    fundId: string | null;
    categoryId: string | null;
    plannedAmountMinor: number;
    notes: string | null;
  }>,
): BudgetLineDraft[] =>
  lines.map((line) => ({
    key: line.id,
    lineType: line.fundId ? 'income' : 'expense',
    fundId: line.fundId,
    categoryId: line.categoryId,
    plannedAmountMinor: line.plannedAmountMinor,
    notes: line.notes ?? '',
  }));
